import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Smartphone, Tablet, Monitor, ChevronDown, ChevronUp, Plus, Minus, GripVertical, AlertTriangle } from 'lucide-react';

/**
 * Breakpoint definitions
 */
const BREAKPOINTS = [
  { id: 'base', label: 'Mobile', short: 'M', icon: Smartphone },
  { id: 'tablet', label: 'Tablet', short: 'T', icon: Tablet },
  { id: 'desktop', label: 'Desktop', short: 'D', icon: Monitor },
];

/**
 * Column layout presets
 * - spans: Array of column spans (out of 12)
 * - null spans means equal distribution based on column count
 */
const COLUMN_LAYOUT_PRESETS = [
  { value: 'equal', label: 'Equal', spans: null },
  { value: '1-2', label: '1/3 + 2/3', spans: [4, 8] },
  { value: '2-1', label: '2/3 + 1/3', spans: [8, 4] },
  { value: '1-3', label: '1/4 + 3/4', spans: [3, 9] },
  { value: '3-1', label: '3/4 + 1/4', spans: [9, 3] },
  { value: '1-1-1', label: '3 Equal', spans: [4, 4, 4] },
  { value: '1-2-1', label: '1/4 + 1/2 + 1/4', spans: [3, 6, 3] },
  { value: 'stacked', label: 'Stacked', spans: [12] },
  { value: 'custom', label: 'Custom', spans: null },
];


/**
 * Small visual preview of column layout
 * Renders bars showing column proportions
 */
function LayoutPreview({ spans, isEqual, isCustom, className = '', small = false }) {
  // Default to 2 equal columns for equal distribution preview
  let previewSpans = spans;

  if (isEqual || (!spans && !isCustom)) {
    previewSpans = [6, 6]; // 2 equal columns
  }

  if (isCustom && !spans) {
    previewSpans = [5, 4, 3]; // Custom preview placeholder
  }

  if (!previewSpans || previewSpans.length === 0) {
    previewSpans = [6, 6];
  }

  const totalSpan = previewSpans.reduce((sum, s) => sum + s, 0);
  const height = small ? 'h-3' : 'h-4';
  const width = small ? 'w-8' : 'w-10';
  const gap = small ? 'gap-0.5' : 'gap-0.5';

  return (
    <div className={`flex ${gap} ${width} ${height} ${className}`}>
      {previewSpans.map((span, idx) => (
        <div
          key={idx}
          className="h-full bg-current rounded-sm"
          style={{ flex: span / totalSpan }}
        />
      ))}
    </div>
  );
}

/**
 * Get the preset for a layout value
 */
function getPresetForValue(value) {
  return COLUMN_LAYOUT_PRESETS.find(p => p.value === value) || COLUMN_LAYOUT_PRESETS[0];
}

/**
 * Resolve current span values from responsive column span objects
 * @param {Array} currentColumnSpans - Array of responsive span objects from column children
 * @param {string} activeBreakpoint - Current breakpoint
 * @returns {number[]} Array of resolved span numbers
 */
function resolveSpans(currentColumnSpans, activeBreakpoint) {
  if (!currentColumnSpans || currentColumnSpans.length === 0) {
    return [6, 6];
  }

  return currentColumnSpans.map((spanObj) => {
    if (typeof spanObj === 'number') return spanObj;
    if (typeof spanObj === 'object' && spanObj !== null) {
      return spanObj[activeBreakpoint] || spanObj.base || 6;
    }
    return 6;
  });
}

/**
 * CustomColumnEditor - Interactive visual column span editor
 *
 * Shows a proportional bar with draggable dividers, numeric inputs,
 * and add/remove column buttons.
 */
function CustomColumnEditor({
  currentColumnSpans,
  activeBreakpoint,
  onCustomSpansChange,
  onAddColumn,
  onRemoveColumn,
  disabled = false,
}) {
  const barRef = useRef(null);
  const [dragging, setDragging] = useState(null);
  const [removeTargetIdx, setRemoveTargetIdx] = useState(null); // null = not confirming, number = column index to remove

  const spans = resolveSpans(currentColumnSpans, activeBreakpoint);
  const totalSpan = spans.reduce((sum, s) => sum + s, 0);

  // Auto-dismiss confirmation after 4 seconds
  useEffect(() => {
    if (removeTargetIdx === null) return;
    const timer = setTimeout(() => setRemoveTargetIdx(null), 4000);
    return () => clearTimeout(timer);
  }, [removeTargetIdx]);

  /**
   * Handle dragging a divider between columns
   */
  const handleMouseDown = useCallback((dividerIndex, e) => {
    if (disabled) return;
    e.preventDefault();
    setDragging(dividerIndex);
  }, [disabled]);

  useEffect(() => {
    if (dragging === null) return;

    const handleMouseMove = (e) => {
      if (!barRef.current) return;

      const rect = barRef.current.getBoundingClientRect();
      const barWidth = rect.width;
      const mouseX = e.clientX - rect.left;

      const gridPos = Math.round((mouseX / barWidth) * 12);
      const spansBefore = spans.slice(0, dragging).reduce((s, v) => s + v, 0);
      const combinedSpan = spans[dragging] + spans[dragging + 1];

      let leftSpan = gridPos - spansBefore;
      leftSpan = Math.max(1, Math.min(combinedSpan - 1, leftSpan));
      const rightSpan = combinedSpan - leftSpan;

      if (leftSpan !== spans[dragging] || rightSpan !== spans[dragging + 1]) {
        const newSpans = [...spans];
        newSpans[dragging] = leftSpan;
        newSpans[dragging + 1] = rightSpan;
        onCustomSpansChange(activeBreakpoint, newSpans);
      }
    };

    const handleMouseUp = () => {
      setDragging(null);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [dragging, spans, activeBreakpoint, onCustomSpansChange]);

  /**
   * Increment or decrement a single column's span
   */
  const adjustSpan = useCallback((columnIndex, delta) => {
    const newSpans = [...spans];
    const newValue = newSpans[columnIndex] + delta;
    if (newValue < 1) return;
    newSpans[columnIndex] = newValue;
    onCustomSpansChange(activeBreakpoint, newSpans);
  }, [spans, activeBreakpoint, onCustomSpansChange]);

  const canAddColumn = spans.length < 12;
  const canRemoveColumn = spans.length > 1;

  return (
    <div className="mt-1.5 space-y-1.5">
      {/* Visual bar with draggable dividers and up/down arrows */}
      <div
        ref={barRef}
        className="relative flex h-10 rounded overflow-hidden border border-gray-200"
        style={{ cursor: dragging !== null ? 'col-resize' : 'default' }}
      >
        {spans.map((span, idx) => (
          <React.Fragment key={idx}>
            <div
              className={`flex flex-col items-center justify-center select-none relative group/col transition-colors
                ${removeTargetIdx === idx
                  ? 'bg-red-100 ring-1 ring-inset ring-red-300'
                  : removeTargetIdx !== null
                    ? 'bg-blue-50 cursor-pointer hover:bg-red-50'
                    : 'bg-blue-50'
                }`}
              style={{ flex: span / totalSpan }}
              title={removeTargetIdx !== null ? `Click to select Column ${idx + 1} for removal` : `Column ${idx + 1}: ${span}`}
              onClick={removeTargetIdx !== null ? () => setRemoveTargetIdx(idx) : undefined}
            >
              {(() => {
                const isTargeted = removeTargetIdx === idx;
                const arrowColor = isTargeted
                  ? 'text-red-400 hover:text-red-600'
                  : 'text-blue-400 hover:text-blue-600';
                const numColor = isTargeted ? 'text-red-700' : 'text-blue-700';
                return (
                  <>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); adjustSpan(idx, 1); }}
                      disabled={disabled || removeTargetIdx !== null}
                      className={`${arrowColor} disabled:opacity-30 leading-none`}
                    >
                      <ChevronUp className="w-3 h-3" />
                    </button>
                    <span className={`text-[10px] font-semibold ${numColor} leading-none`}>{span}</span>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); adjustSpan(idx, -1); }}
                      disabled={disabled || span <= 1 || removeTargetIdx !== null}
                      className={`${arrowColor} disabled:opacity-30 leading-none`}
                    >
                      <ChevronDown className="w-3 h-3" />
                    </button>
                  </>
                );
              })()}
            </div>
            {idx < spans.length - 1 && (
              <div
                className={`w-1 flex-shrink-0 cursor-col-resize flex items-center justify-center z-10
                  ${dragging === idx
                    ? 'bg-blue-500'
                    : 'bg-gray-300 hover:bg-blue-400'
                  } transition-colors`}
                onMouseDown={(e) => handleMouseDown(idx, e)}
                title="Drag to resize"
              >
                <GripVertical className="w-2.5 h-2.5 text-white pointer-events-none" />
              </div>
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Add / Remove column buttons */}
      <div className="flex items-center justify-between">
        <span className="text-[10px] text-gray-400">
          {spans.length} col{spans.length !== 1 ? 's' : ''} &middot; {spans.join(' + ')} = {totalSpan}
        </span>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => {
              if (disabled || !canRemoveColumn) return;
              setRemoveTargetIdx(spans.length - 1);
            }}
            disabled={disabled || !canRemoveColumn}
            className="p-0.5 rounded border border-gray-200 text-gray-500
              hover:bg-gray-50 hover:text-gray-700
              disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            title="Remove last column"
          >
            <Minus className="w-3 h-3" />
          </button>
          <button
            type="button"
            onClick={() => !disabled && canAddColumn && onAddColumn()}
            disabled={disabled || !canAddColumn}
            className="p-0.5 rounded border border-gray-200 text-gray-500
              hover:bg-gray-50 hover:text-gray-700
              disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            title="Add column"
          >
            <Plus className="w-3 h-3" />
          </button>
        </div>
      </div>

      {/* Remove column confirmation */}
      {removeTargetIdx !== null && (
        <div className="flex items-center gap-1.5 p-1.5 bg-amber-50 border border-amber-200 rounded text-[10px]">
          <AlertTriangle className="w-3 h-3 text-amber-500 flex-shrink-0" />
          <span className="text-amber-700 flex-1">
            Remove <strong>Column {removeTargetIdx + 1}</strong>? Content will be lost.
          </span>
          <button
            type="button"
            onClick={() => {
              onRemoveColumn(removeTargetIdx);
              setRemoveTargetIdx(null);
            }}
            className="px-1.5 py-0.5 bg-red-500 text-white rounded text-[10px] font-medium hover:bg-red-600 transition-colors"
          >
            Remove
          </button>
          <button
            type="button"
            onClick={() => setRemoveTargetIdx(null)}
            className="px-1.5 py-0.5 bg-gray-200 text-gray-600 rounded text-[10px] font-medium hover:bg-gray-300 transition-colors"
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * ResponsiveColumnLayoutField - Compact column layout selector with breakpoint support
 *
 * Props:
 * - field, value, onChange, disabled, compact, activeBreakpoint, onBreakpointChange
 *   (existing props for preset selection)
 * - currentColumnSpans: Array of responsive span objects from the row's column children
 * - onCustomSpansChange(breakpoint, newSpansArray): Update spans for all columns at a breakpoint
 * - onAddColumn(): Add a new column to the row
 * - onRemoveColumn(columnIndex): Remove a column from the row
 */
export default function ResponsiveColumnLayoutField({
  field,
  value,
  onChange,
  disabled = false,
  compact = true,
  activeBreakpoint: externalBreakpoint,
  onBreakpointChange,
  currentColumnSpans,
  onCustomSpansChange,
  onAddColumn,
  onRemoveColumn,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [localBreakpoint, setLocalBreakpoint] = useState('base');
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);

  // Use external breakpoint if provided, otherwise use local state
  const activeBreakpoint = externalBreakpoint ?? localBreakpoint;

  const handleBreakpointChange = (bp) => {
    if (onBreakpointChange) {
      onBreakpointChange(bp);
    } else {
      setLocalBreakpoint(bp);
    }
  };

  // Normalize to responsive format
  const normalizeValue = (val) => {
    if (typeof val === 'object' && val !== null) {
      if (val.base !== undefined) {
        return {
          base: val.base || 'equal',
          tablet: val.tablet || null,
          desktop: val.desktop || null,
        };
      }
      return { base: 'equal', tablet: null, desktop: null };
    }
    if (typeof val === 'string') {
      return { base: val, tablet: null, desktop: null };
    }
    return field.default || { base: 'equal', tablet: null, desktop: null };
  };

  const responsiveObj = normalizeValue(value);

  // Get current layout for active breakpoint (with fallback to base)
  const currentLayout = responsiveObj[activeBreakpoint] || responsiveObj.base || 'equal';
  const currentPreset = getPresetForValue(currentLayout);

  const updateLayout = (newLayout) => {
    const newResponsiveObj = {
      ...responsiveObj,
      [activeBreakpoint]: newLayout,
    };
    onChange(newResponsiveObj);
    setIsOpen(false);
  };

  const clearOverride = () => {
    if (activeBreakpoint !== 'base') {
      const newResponsiveObj = {
        ...responsiveObj,
        [activeBreakpoint]: null,
      };
      onChange(newResponsiveObj);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e) => {
      if (
        triggerRef.current &&
        !triggerRef.current.contains(e.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target)
      ) {
        setIsOpen(false);
      }
    };

    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  // Calculate dropdown position
  const getDropdownPosition = () => {
    if (!triggerRef.current) return { top: 0, left: 0 };
    const rect = triggerRef.current.getBoundingClientRect();
    return {
      top: rect.bottom + 4,
      left: rect.left - rect.width, // Centered, assuming dropdown width of 200px
    };
  };

  const hasOverride = activeBreakpoint !== 'base' && responsiveObj[activeBreakpoint];
  const isInheriting = activeBreakpoint !== 'base' && !responsiveObj[activeBreakpoint];
  const isCustom = currentLayout === 'custom';

  // Resolve current spans for the LayoutPreview when custom is selected
  const customSpans = isCustom
    ? resolveSpans(currentColumnSpans, activeBreakpoint)
    : null;

  return (
    <div className="py-0.5">
      <div className="flex items-center gap-2">
        <label className="text-xs text-gray-500 w-16 flex-shrink-0 truncate">
          {field.label}
        </label>

        {/* Breakpoint pills */}
        <div className="flex items-center gap-0.5 bg-gray-100 rounded p-0.5">
          {BREAKPOINTS.map((bp) => {
            const Icon = bp.icon;
            const bpHasOverride = bp.id !== 'base' && responsiveObj[bp.id];
            return (
              <button
                key={bp.id}
                type="button"
                onClick={() => handleBreakpointChange(bp.id)}
                disabled={disabled}
                title={bp.label}
                className={`relative p-1 rounded transition-colors
                  ${activeBreakpoint === bp.id
                    ? 'bg-white shadow-sm text-gray-900'
                    : 'text-gray-500 hover:text-gray-700'
                  } ${disabled ? 'opacity-50' : ''}`}
              >
                <Icon className="w-3 h-3" />
                {bpHasOverride && (
                  <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 bg-blue-500 rounded-full" />
                )}
              </button>
            );
          })}
        </div>

        {/* Layout selector trigger */}
        <button
          ref={triggerRef}
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          className={`flex items-center gap-1.5 px-2 py-1 border rounded text-xs
            ${isOpen ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white hover:border-gray-300'}
            ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
            ${isInheriting ? 'text-gray-400 italic' : 'text-gray-700'}`}
        >
          <LayoutPreview
            spans={isCustom ? customSpans : currentPreset.spans}
            isEqual={currentLayout === 'equal'}
            isCustom={isCustom}
            small
            className={isInheriting ? 'text-gray-300' : 'text-gray-400'}
          />
          <span className="max-w-[60px] truncate">
            {isInheriting ? 'Inherit' : currentPreset.label}
          </span>
          <ChevronDown className={`w-3 h-3 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>
      </div>

      {/* Custom Column Editor - shown below the selector when layout is 'custom' */}
      {isCustom && onCustomSpansChange && (
        <CustomColumnEditor
          currentColumnSpans={currentColumnSpans}
          activeBreakpoint={activeBreakpoint}
          onCustomSpansChange={onCustomSpansChange}
          onAddColumn={onAddColumn}
          onRemoveColumn={onRemoveColumn}
          disabled={disabled}
        />
      )}

      {/* Dropdown */}
      {isOpen && createPortal(
        <div
          ref={dropdownRef}
          className="fixed z-[9999] bg-white rounded-lg shadow-lg border border-gray-200 p-2 w-[200px]"
          style={getDropdownPosition()}
        >
          {/* Presets grid */}
          <div className="grid grid-cols-2 gap-1">
            {COLUMN_LAYOUT_PRESETS.map((preset) => {
              const isActive = currentLayout === preset.value;
              return (
                <button
                  key={preset.value}
                  type="button"
                  onClick={() => updateLayout(preset.value)}
                  className={`flex items-center gap-2 px-2 py-1.5 rounded text-xs transition-colors
                    ${isActive
                      ? 'bg-blue-50 text-blue-700 border border-blue-200'
                      : 'hover:bg-gray-50 text-gray-600 border border-transparent'
                    }`}
                >
                  <LayoutPreview
                    spans={preset.spans}
                    isEqual={preset.value === 'equal'}
                    isCustom={preset.value === 'custom'}
                    small
                    className={isActive ? 'text-blue-500' : 'text-gray-400'}
                  />
                  <span className="truncate">{preset.label}</span>
                </button>
              );
            })}
          </div>

          {/* Clear override */}
          {hasOverride && (
            <div className="mt-2 pt-2 border-t border-gray-100">
              <button
                onClick={() => {
                  clearOverride();
                  setIsOpen(false);
                }}
                className="w-full text-[10px] text-red-600 hover:text-red-700 hover:bg-red-50 px-2 py-1 rounded transition-colors text-left"
              >
                Clear {activeBreakpoint} override
              </button>
            </div>
          )}

          {/* Inheritance info */}
          {isInheriting && (
            <div className="mt-2 pt-2 border-t border-gray-100">
              <div className="text-[10px] text-gray-500 px-1">
                Inheriting "{getPresetForValue(responsiveObj.base).label}" from Mobile
              </div>
            </div>
          )}
        </div>,
        document.body
      )}
    </div>
  );
}
